// CONTRACT:C4-DESIGN-TOKENS.1.0
// COP — VerdictGlyph (Item 5, floor #4). React shell only; never enters the
// render loop. Spec: docs/ux/SHELL-COMPOSITION.md §2.2.
//
// THE ONE STATE-OF-THE-PICTURE INSTRUMENT at the top of the left gutter. It is a
// SHAPE-PRIMARY face — NOT a colour-only badge:
//   • quiet  → a calm ring (no inner mark): nothing is raising the picture.
//   • watch  → a hollow diamond: a hostile/elevated entity is present.
//   • alert  → a filled triangle: a live alert is raising the picture.
// The SHAPE carries the verdict; colour merely seconds it (R-LAW-2: shape is the
// always-on encoding, motion is the urgency channel — no 6th always-on channel).
//
// MOTION = URGENCY ONLY (ACP-125): routine is static; priority/immediate/flash
// add an escalating pulse on the glyph ALONE, gated on prefers-reduced-motion
// (global rule at style.css:511, plus a local guard in verdict.css).
//
// Click opens a LIGHT drill popover listing what raised the verdict. The popover
// is a light surface: it does NOT claim the R-LAW-3 heavy slot — it only registers
// with the shared Esc stack so one Esc dismisses it. Each reason row drills to the
// raising entity via handle.locate (fly+halo) + handle.select (Ledger re-skin).
// The popover's CLEAR ALL footer dismisses the raising alerts from the operator's
// SELF-SCOPE log (handle.clearAllAlerts) — the host's verdict counts only
// un-dismissed alerts, so clearing quiets the glyph/flash for THIS operator only.
// A view dismiss, never a denial: the objects stay on the map and a re-emit
// re-raises the verdict (unread). Persisted per user (survives reload).
//
// Reads status.verdict DIRECTLY. Every reason is already over the cleared-VISIBLE
// set (Glomar): there is no padlock, no counted gap, no "Access Denied" here.

import { useRef, useState } from "react";
import type { CopStatus, CopHandle } from "../host/cop.ts";
import type {
  VerdictFace,
  VerdictUrgency,
  VerdictReason,
} from "../host/shellContract.ts";
import { useEscapeStack } from "./useEscapeStack.ts";
import "./verdict.css";

/** Human-legible name for each shape-primary face (the accessible/title label —
 *  the visible encoding is the SHAPE, this is the words for it). */
const FACE_LABEL: Record<VerdictFace, string> = {
  quiet: "Quiet",
  watch: "Watch",
  alert: "Alert",
};

/** ACP-125 precedence words for the urgency channel, surfaced in the drill header
 *  and the glyph's accessible name. Motion encodes the same precedence. */
const URGENCY_LABEL: Record<VerdictUrgency, string> = {
  routine: "Routine",
  priority: "Priority",
  immediate: "Immediate",
  flash: "Flash",
};

/** The shape-primary mark for a face, drawn in pure SVG geometry so the SHAPE — not
 *  the colour — is the legible channel. The outer ring is shared housing; the inner
 *  mark differentiates: none (quiet) / hollow diamond (watch) / filled triangle
 *  (alert). currentColor lets the face token tint without becoming the encoding. */
function FaceMark({ face }: { face: VerdictFace }): JSX.Element {
  return (
    <svg
      className="verdict__svg"
      viewBox="0 0 32 32"
      aria-hidden="true"
      focusable="false"
    >
      {/* shared housing ring — present on every face so the gutter slot reads as
          one persistent instrument, not a flashing icon. */}
      <circle className="verdict__ring" cx="16" cy="16" r="13" />
      {face === "watch" && (
        // hollow diamond — "something is up, not yet alarming".
        <rect
          className="verdict__mark verdict__mark--diamond"
          x="9.5"
          y="9.5"
          width="13"
          height="13"
          transform="rotate(45 16 16)"
        />
      )}
      {face === "alert" && (
        // filled triangle — the universal caution silhouette.
        <polygon
          className="verdict__mark verdict__mark--triangle"
          points="16,7 25,23 7,23"
        />
      )}
      {/* quiet → no inner mark; the calm ring alone is the face. */}
    </svg>
  );
}

/** One drill row in the light popover. The whole row is the keyboard/click target;
 *  activating it flies+haloes (locate) AND re-skins the Ledger (select). */
function ReasonRow({
  reason,
  onDrill,
}: {
  reason: VerdictReason;
  onDrill: (id: string) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className="verdict-drill__row"
      onClick={() => onDrill(reason.id)}
    >
      <span className="verdict-drill__dot" aria-hidden="true" />
      <span className="verdict-drill__label">{reason.label}</span>
    </button>
  );
}

export function VerdictGlyph({
  status,
  handle,
  variant = "gutter",
}: {
  status: CopStatus;
  handle: CopHandle;
  /** Render form. "gutter" = the original full-width column instrument (label
   *  below the glyph). "rail" = the 54px FUNCTION RAIL form: a compact 40×40
   *  instrument with no word-label (the rail is icon-only) — behavior unchanged. */
  variant?: "gutter" | "rail";
}): JSX.Element {
  const { face, urgency, reasons } = status.verdict;
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // The drill popover is a LIGHT surface — it never claims the heavy slot. One Esc
  // pops exactly this level (LIFO via the shared stack), only while it's open.
  useEscapeStack(() => setOpen(false), open);

  const hasReasons = reasons.length > 0;

  // Drill: fly+halo the raising entity, then re-skin the Ledger to it, then close
  // the light popover (a drill is a one-shot navigation, not a sticky panel).
  const drill = (id: string): void => {
    handle.locate(id);
    handle.select(id);
    setOpen(false);
  };

  // A click toggles the popover. With nothing raising the picture there is nothing
  // to drill into, so the glyph is a calm read-only instrument (no popover).
  const toggle = (): void => {
    if (!hasReasons) return;
    setOpen((o) => !o);
  };

  const accessibleName = `Verdict: ${FACE_LABEL[face]} — ${URGENCY_LABEL[urgency]}`;

  const rail = variant === "rail";

  return (
    <div className="verdict" ref={rootRef} data-face={face} data-urgency={urgency} data-variant={variant}>
      <button
        type="button"
        className="verdict__btn"
        aria-label={accessibleName}
        aria-haspopup={hasReasons ? "true" : undefined}
        aria-expanded={hasReasons ? open : undefined}
        title={accessibleName}
        data-actionable={hasReasons ? "true" : "false"}
        onClick={toggle}
      >
        <FaceMark face={face} />
        {/* The word-label seconds the shape for legibility — never the sole
            channel. Tabular so the slot width never jitters between faces. In the
            54px rail there is no room for a word-label (icon-only); the title +
            aria-label still carry the words. */}
        {!rail && <span className="verdict__face-label">{FACE_LABEL[face]}</span>}
      </button>

      {open && hasReasons && (
        <div
          className="verdict-drill panel"
          role="dialog"
          aria-label="What raised the verdict"
        >
          <div className="verdict-drill__head">
            <span className="verdict-drill__title">
              {FACE_LABEL[face]} · {URGENCY_LABEL[urgency]}
            </span>
            <span className="verdict-drill__count">{reasons.length}</span>
          </div>
          <div className="verdict-drill__list">
            {reasons.map((r) => (
              <ReasonRow key={r.id} reason={r} onDrill={drill} />
            ))}
          </div>
          <button
            type="button"
            className="verdict-drill__clear"
            title="Dismiss these alerts from your log (a re-emit re-surfaces them)"
            onClick={() => {
              // Self-scope clear: quiets the glyph for this operator; the alerts stay
              // on the map/registry. Close the popover — nothing left to drill.
              handle.clearAllAlerts();
              setOpen(false);
            }}
          >
            CLEAR ALL
          </button>
        </div>
      )}
    </div>
  );
}
